import React, { useEffect, useState } from 'react';
import { Trash } from 'lucide-react';
import HourglassBottomIcon from '@mui/icons-material/HourglassBottom';
import BuildCircleIcon from '@mui/icons-material/BuildCircle';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { Dialog, DialogActions, DialogContent, DialogTitle, Button, TextField, Grid, Paper, Typography, Select, MenuItem, FormControl, InputLabel } from '@mui/material';
import { motion } from 'framer-motion';
import { createTask, deleteTask, getTasksByProject } from '../../api/ProjectTaskApi';

const TaskList = ({ project, userId }) => {
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [newTask, setNewTask] = useState({ projectId: project.id, title: '', description: '', priority: 'Medium' });
    const [tasks, setTasks] = useState([]);
    console.log('project===.', project);

    // Modal handlers
    const handleOpenModal = () => setIsModalOpen(true);
    const handleCloseModal = () => setIsModalOpen(false);

    // Fetch tasks when the component is mounted or project ID changes
    useEffect(() => {
        const fetchTasks = async () => {
            try {
                const taskData = await getTasksByProject(project.id);
                setTasks(taskData.tasks || []);
            } catch (error) {
                console.error('Error fetching tasks:', error);
                setTasks([]);
            }
        };
        fetchTasks();
    }, [project.id]);

    // Add a new task
    const handleAddTask = async (id) => {
        try {
            const addedTask = await createTask(id, newTask);
            setTasks((prevTasks) => [...prevTasks, addedTask]);
            setNewTask({ title: '', description: '', priority: 'Medium' });
            handleCloseModal();
        } catch (error) {
            console.error('Error adding task:', error);
        }
    };

    // Delete a task
    const handleDeleteTask = async (taskId) => {
        try {
            await deleteTask(taskId);
            setTasks((prevTasks) => prevTasks.filter((task) => task.id !== taskId));
        } catch (error) {
            console.error('Error deleting task:', error);
        }
    };
    const updateTaskStatus = (projectId, taskId, newStatus) => {
        setTasks((prevTasks) =>
          prevTasks.map((task) =>
            task.id === taskId ? { ...task, status: newStatus } : task
          )
        );
      };
    return (
        <div className="p-6">
            <Typography variant="h5" gutterBottom className="text-primary mb-6">
                <strong>Manage Tasks</strong>
            </Typography>
            {
                userId === project.userId &&
                <Button
                    onClick={handleOpenModal}
                    variant="contained"
                    color="primary"
                    className="mb-4 hover:scale-105 transition-all duration-300">
                    Add New Task
                </Button>
            }

            <Grid container spacing={3} py={5}>
                {tasks?.map((task) => (
                    <Grid item xs={12} sm={6} md={6} key={task.id}>
                        <motion.div
                            initial={{ opacity: 0, scale: 0.8 }}
                            animate={{ opacity: 1, scale: 1 }}
                            transition={{ type: 'spring', stiffness: 100, damping: 20 }}
                        >
                            <Paper elevation={5} className="p-4 rounded-lg shadow-lg bg-gray-100 text-white transition-all duration-300">
                                <Grid container justifyContent="space-between">
                                    {/* Priority Indicator */}
                                    <Grid container justifyContent="flex-start" spacing={1} className="mt-2">
                                        <Grid item md={2}>
                                            <Typography variant="caption" color={task.priority === 'High' ? 'error' : task.priority === 'Medium' ? 'warning' : 'success'}>
                                                {task.priority} Priority
                                            </Typography>
                                        </Grid>
                                        {/* Status with Icon */}
                                        <Grid item xs={4} md={6}>
                                            <Grid container justifyContent="center" alignItems="center">
                                                {task.status === 'Pending' && <HourglassBottomIcon className="text-yellow-300" />}
                                                {task.status === 'In Progress' && <BuildCircleIcon className="text-blue-300" />}
                                                {task.status === 'Completed' && <CheckCircleIcon className="text-green-300" />}
                                                <Typography className="font-medium">
                                                    {task.status}
                                                </Typography>
                                            </Grid>
                                        </Grid>
                                        <Grid item xs={4} md={4} display="flex" justifyContent="flex-end">
                                            {userId === project.userId &&

                                                <Button
                                                    onClick={() => handleDeleteTask(task.id)}
                                                    variant="outlined"
                                                    color="error"
                                                    size="small"
                                                    sx={{ transform: 'scale(1)', transition: 'transform 0.3s', '&:hover': { transform: 'scale(1.1)' } }}
                                                >
                                                    <Trash size={16} />
                                                </Button>
                                            }
                                        </Grid>
                                    </Grid>

                                    {/* Actions: Status Select and Delete */}
                                    <Grid container justifyContent="space-between" alignItems="center" className="pt-3">
                                        <Grid item xs={8} md={8}>
                                            <Typography variant="body1" className="font-bold text-lg">
                                                {task.title}
                                            </Typography>
                                        </Grid>
                                        <Grid item xs={4} md={4}>
                                            {userId === project.userId &&
                                                <FormControl variant="outlined" size="small" fullWidth>
                                                    <InputLabel>Status</InputLabel>
                                                    <Select
                                                        value={task.status}
                                                        onChange={(e) => updateTaskStatus(project.id, task.id, e.target.value)}
                                                        label="Status"
                                                    >
                                                        <MenuItem value="Pending">Pending</MenuItem>
                                                        <MenuItem value="In Progress">In Progress</MenuItem>
                                                        <MenuItem value="Completed">Completed</MenuItem>
                                                    </Select>
                                                </FormControl>
                                            }
                                        </Grid>
                                    </Grid>
                                </Grid>
                            </Paper>
                        </motion.div>
                    </Grid>
                ))}
            </Grid>

            {/* Add Task Modal */}
            <Dialog open={isModalOpen} onClose={handleCloseModal}>
                <DialogTitle>Add New Task</DialogTitle>
                <DialogContent>
                    <TextField
                        label="Task Title"
                        value={newTask.title}
                        onChange={(e) => setNewTask({ ...newTask, title: e.target.value })}
                        fullWidth
                        variant="outlined"
                        margin="normal"
                    />
                    <TextField
                        label="Task Description"
                        value={newTask.description}
                        onChange={(e) => setNewTask({ ...newTask, description: e.target.value })}
                        fullWidth
                        variant="outlined"
                        margin="normal"
                        multiline
                        rows={4}
                    />
                    <FormControl variant="outlined" fullWidth margin="normal">
                        <InputLabel>Priority</InputLabel>
                        <Select
                            value={newTask.priority}
                            onChange={(e) => setNewTask({ ...newTask, priority: e.target.value })}
                            label="Priority"
                        >
                            <MenuItem value="Low">Low</MenuItem>
                            <MenuItem value="Medium">Medium</MenuItem>
                            <MenuItem value="High">High</MenuItem>
                        </Select>
                    </FormControl>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCloseModal} color="secondary">
                        Cancel
                    </Button>
                    <Button onClick={() => handleAddTask(project.id)} color="primary">
                        Add Task
                    </Button>
                </DialogActions>
            </Dialog>
        </div>
    );
};

export default TaskList;
